/**
 * Authentication Guard Module
 * Provides automatic authentication checking and redirect functionality
 * Eliminates the need for repeated Firebase setup on protected pages
 */

import AuthManager from './auth-manager.js';
import { FIREBASE_CONFIG } from '../firebase-config.js';
import { User } from 'firebase/auth';

export interface AuthGuardOptions {
  requireAuth?: boolean;
  redirectUrl?: string | null;
  onAuthReady?: ((user: User | null, isAuthenticated: boolean) => void) | null;
  onAuthChange?: ((user: User | null, isAuthenticated: boolean) => void) | null;
}

export interface AuthState {
  isAuthenticated: boolean;
  currentUser: User | null;
  authReady: boolean;
}

class AuthGuard {
  private authManager: AuthManager | null;
  private isAuthenticated: boolean;
  private currentUser: User | null;
  private authReady: boolean;
  private redirectUrl: string | null;

  constructor() {
    this.authManager = null;
    this.isAuthenticated = false;
    this.currentUser = null;
    this.authReady = false;
    this.redirectUrl = null;
  }

  /**
     * Initialize authentication and check if user is authenticated
     * Automatically redirects to sign-in if not authenticated
     * @param {AuthGuardOptions} options - Configuration options
     */
  async initialize(options: AuthGuardOptions = {}): Promise<boolean> {
    // Playwright E2E test bypass — set window.__PLAYWRIGHT_TEST_USER__ before page loads
    // to skip Firebase initialization entirely and use a mock authenticated state.
    if (typeof window !== 'undefined' && (window as any).__PLAYWRIGHT_TEST_USER__) {
      const { requireAuth = true, onAuthReady = null } = options;
      const testUser = (window as any).__PLAYWRIGHT_TEST_USER__ as User;
      this.currentUser = testUser;
      this.isAuthenticated = true;
      this.authReady = true;
      this.authManager = {
        isInitialized: true,
        onAuthStateChanged: (_cb: (user: User | null) => void) => { /* noop */ },
        getIdToken: async () => 'e2e-test-token',
        authenticatedFetch: (url: string, opts: RequestInit = {}) =>
          fetch(url, {
            ...opts,
            headers: { Authorization: 'Bearer e2e-test-token', ...(opts.headers as Record<string, string>) },
          }),
      } as unknown as AuthManager;
      if (onAuthReady) onAuthReady(testUser, true);
      if (requireAuth && !this.isAuthenticated) return false;
      return true;
    }

    // Prevent multiple initializations
    if (this.authReady) {
      console.log('Auth guard already initialized, returning current state');
      const { requireAuth = true } = options;
      if (requireAuth && !this.isAuthenticated) {
        this.redirectToSignIn();
        return false;
      }
      return this.isAuthenticated || !requireAuth;
    }
    const {
      requireAuth = true,
      redirectUrl = null,
      onAuthReady = null,
      onAuthChange = null
    } = options;

    this.redirectUrl = redirectUrl;

    try {
      // Initialize AuthManager and wait for stabilized auth state
      console.log('Auth guard: Initializing Firebase...');
      this.authManager = new AuthManager();
      await this.authManager.initialize(FIREBASE_CONFIG as any);

      // authManager.initialize() has already waited for Firebase's definitive initial
      // auth state (after IndexedDB/localStorage session restore), so the state is
      // available immediately — no stabilization timer needed.
      this.currentUser = this.authManager!.getCurrentUser();
      this.isAuthenticated = !!this.currentUser;
      this.authReady = true;

      console.log('Auth guard: Auth state ready', {
        user: this.currentUser?.email ?? 'none',
        isAuthenticated: this.isAuthenticated,
        page: window.location.pathname,
      });

      // Register for live auth changes (sign-out, token revocation, etc.).
      // onAuthStateChanged fires immediately with current state since isInitialized
      // is true; skip that first call since state was already read above.
      let isFirstCallback = true;
      this.authManager!.onAuthStateChanged((user) => {
        if (isFirstCallback) {
          isFirstCallback = false;
          return;
        }
        this.currentUser = user;
        this.isAuthenticated = !!user;
        if (onAuthChange) onAuthChange(user, this.isAuthenticated);
      });

      if (onAuthReady) {
        onAuthReady(this.currentUser, this.isAuthenticated);
      }

      // Check if authentication is required and user is not authenticated
      if (requireAuth && !this.isAuthenticated) {
        console.log('Auth guard: User not authenticated, redirecting', {
          requireAuth,
          isAuthenticated: this.isAuthenticated,
          redirectUrl,
          currentUrl: window.location.href
        });
        if (redirectUrl && redirectUrl !== window.location.href) {
          // Custom redirect URL specified (like back to splash page)
          console.log('Auth guard: Redirecting to custom URL:', redirectUrl);
          window.location.href = redirectUrl;
        } else {
          // Default behavior - redirect to sign-in
          console.log('Auth guard: Redirecting to sign-in');
          this.redirectToSignIn();
        }
        return false; // Indicates redirect happened
      }

      console.log('Auth guard initialized successfully', {
        isAuthenticated: this.isAuthenticated,
        user: this.currentUser?.email || 'none',
        page: window.location.pathname
      });
      return true; // Indicates user can proceed

    } catch (error) {
      console.error('Failed to initialize auth guard:', error);

      // If auth is required but failed, redirect appropriately
      if (requireAuth) {
        if (redirectUrl && redirectUrl !== window.location.href) {
          window.location.href = redirectUrl;
        } else {
          this.redirectToSignIn();
        }
        return false;
      }

      // If auth is not required, allow page to function
      this.authReady = true;
      return true;
    }
  }

  /**
     * Redirect to sign-in page with current page as return URL
     */
  redirectToSignIn(): void {
    const signInUrl = new URL('signin.html', window.location.origin);
    window.location.href = signInUrl.toString();
  }

  /**
     * Get the auth manager instance
     * @returns {AuthManager | null} Auth manager instance
     */
  getAuthManager(): AuthManager | null {
    return this.authManager;
  }

  /**
     * Get current authentication state
     * @returns {AuthState} Authentication state
     */
  getAuthState(): AuthState {
    return {
      isAuthenticated: this.isAuthenticated,
      currentUser: this.currentUser,
      authReady: this.authReady
    };
  }

  /**
     * Make an authenticated API call
     * @param {string} url - API endpoint URL
     * @param {RequestInit} options - Fetch options
     * @returns {Promise<Response>} Fetch response
     */
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    if (!this.authManager) {
      throw new Error('Auth guard not initialized');
    }

    // Wait for auth to be ready
    while (!this.authReady) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!this.isAuthenticated) {
      throw new Error('User not authenticated');
    }

    return this.authManager.authenticatedFetch(url, options);
  }

  /**
     * Sign out and redirect to splash page
     */
  async signOut(): Promise<void> {
    // Clear Firebase auth
    if (this.authManager) {
      await this.authManager.signOut();
    }

    // Update local state
    this.currentUser = null;
    this.isAuthenticated = false;

    window.location.href = 'index.html';
  }
}

// Create a singleton instance
const authGuard = new AuthGuard();

// Export both the class and singleton for different use cases
export default AuthGuard;
export { authGuard };

/**
 * Quick setup function for pages that require authentication
 * Call this at the top of your page's script to automatically handle auth
 * @param {AuthGuardOptions} options - Configuration options (same as AuthGuard.initialize)
 * @returns {Promise<boolean>} True if user can proceed, false if redirected
 */
export async function requireAuth(options: AuthGuardOptions = {}): Promise<boolean> {
  return authGuard.initialize({ requireAuth: true, ...options });
}

/**
 * Setup function for pages that optionally use authentication
 * @param {AuthGuardOptions} options - Configuration options (same as AuthGuard.initialize)
 * @returns {Promise<boolean>} Always true (no redirect)
 */
export async function optionalAuth(options: AuthGuardOptions = {}): Promise<boolean> {
  return authGuard.initialize({ requireAuth: false, ...options });
}